import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Award, CheckCircle, RotateCcw, Send, Loader2,
  ChevronDown, ChevronRight, ChevronLeft, MessageSquare,
  TrendingUp, Users
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import KPIMetricCard from './KPIMetricCard';
import ReportPrintHeader from './ReportPrintHeader';

const statusBadge = (status) => {
  const map = {
    PUBLISHED: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    PENDING_APPROVAL: { label: 'Pending', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
    DRAFT: { label: 'Draft', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  };
  const s = map[status] || { label: status, cls: 'bg-gray-100 text-gray-600' };
  return <Badge className={`${s.cls} text-[10px] border`}>{s.label}</Badge>;
};

const actionBadge = (action) => {
  const map = {
    'Submitted for Approval': { icon: Send, cls: 'text-blue-600 bg-blue-50' },
    'Approved': { icon: CheckCircle, cls: 'text-emerald-600 bg-emerald-50' },
    'Returned to Auditor': { icon: RotateCcw, cls: 'text-amber-600 bg-amber-50' },
  };
  const a = map[action] || { icon: Send, cls: 'text-gray-600 bg-gray-50' };
  const Icon = a.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium ${a.cls}`}>
      <Icon className="h-3 w-3" />{action}
    </span>
  );
};

export default function PreparationQualityReport({
  data, loading, reportTitle, startDate, endDate, region
}) {
  const [expandedReport, setExpandedReport] = useState(null);
  const [viewMode, setViewMode] = useState('auditors'); // 'auditors' | 'reports'
  const [page, setPage] = useState(0);
  const pageSize = 25;

  const kpis = data?.kpis || {};
  const auditorBreakdown = data?.auditor_breakdown || [];
  const reportDetails = data?.report_details || [];

  const totalPages = Math.ceil(reportDetails.length / pageSize);
  const paginatedReports = reportDetails.slice(page * pageSize, (page + 1) * pageSize);

  return (
    <div className="space-y-6 print-section" data-testid="preparation-quality-report">
      <ReportPrintHeader title={reportTitle} startDate={startDate} endDate={endDate} region={region} />

      {/* KPI Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 print:grid-cols-6">
        <KPIMetricCard
          icon={Send}
          title="Submitted"
          description="Reports submitted"
          value={kpis.total_submitted ?? '\u2014'}
          testId="kpi-total-submitted"
        />
        <KPIMetricCard
          icon={Award}
          title="First-Time Approvals"
          description="Approved first try"
          value={kpis.first_time_approvals ?? '\u2014'}
          testId="kpi-first-time-approvals"
          iconBgClass="bg-emerald-100"
          iconClass="text-emerald-600"
        />
        <KPIMetricCard
          icon={TrendingUp}
          title="First-Time Rate"
          description="Quality metric"
          value={kpis.first_time_rate != null ? `${kpis.first_time_rate}%` : '\u2014'}
          testId="kpi-first-time-rate"
          iconBgClass="bg-blue-100"
          iconClass="text-blue-600"
        />
        <KPIMetricCard
          icon={RotateCcw}
          title="Returns"
          description="Sent back for changes"
          value={kpis.total_returned ?? '\u2014'}
          testId="kpi-total-returned"
          iconBgClass="bg-amber-100"
          iconClass="text-amber-600"
        />
        <KPIMetricCard
          icon={CheckCircle}
          title="Approved"
          description="Total approved"
          value={kpis.total_approved ?? '\u2014'}
          testId="kpi-total-approved"
          iconBgClass="bg-green-100"
          iconClass="text-green-600"
        />
        <KPIMetricCard
          icon={TrendingUp}
          title="Avg Rounds"
          description="Submissions to approval"
          value={kpis.avg_rounds_to_approval != null ? kpis.avg_rounds_to_approval : '\u2014'}
          testId="kpi-avg-rounds"
          iconBgClass="bg-purple-100"
          iconClass="text-purple-600"
        />
      </div>

      {/* View toggle */}
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant={viewMode === 'auditors' ? 'default' : 'outline'}
          onClick={() => setViewMode('auditors')}
          data-testid="view-auditors-btn"
        >
          <Users className="h-3.5 w-3.5 mr-1.5" />Auditor Breakdown
        </Button>
        <Button
          size="sm"
          variant={viewMode === 'reports' ? 'default' : 'outline'}
          onClick={() => { setViewMode('reports'); setPage(0); }}
          data-testid="view-reports-btn"
        >
          <MessageSquare className="h-3.5 w-3.5 mr-1.5" />Report Details
        </Button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Auditor Breakdown Table */}
      {!loading && viewMode === 'auditors' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Auditor Performance
            </CardTitle>
            <CardDescription>First-time approval rate by auditor</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Auditor</TableHead>
                    <TableHead className="text-center">Submitted</TableHead>
                    <TableHead className="text-center">Approved</TableHead>
                    <TableHead className="text-center">First-Time Approvals</TableHead>
                    <TableHead className="text-center">Returns</TableHead>
                    <TableHead className="text-center">First-Time Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditorBreakdown.map(a => (
                    <TableRow key={a.auditor_id} data-testid={`auditor-row-${a.auditor_id}`}>
                      <TableCell className="font-medium">{a.auditor_name}</TableCell>
                      <TableCell className="text-center tabular-nums">{a.total_submitted}</TableCell>
                      <TableCell className="text-center tabular-nums">{a.total_approved}</TableCell>
                      <TableCell className="text-center tabular-nums">{a.first_time_approvals}</TableCell>
                      <TableCell className="text-center tabular-nums">{a.total_returns}</TableCell>
                      <TableCell className="text-center">
                        <span className={`font-medium tabular-nums ${
                          a.first_time_rate >= 80 ? 'text-emerald-600' :
                          a.first_time_rate >= 60 ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {a.first_time_rate}%
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                  {auditorBreakdown.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No reports submitted for approval in this period
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Report Details with Timeline */}
      {!loading && viewMode === 'reports' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Report Approval History
            </CardTitle>
            <CardDescription>
              Full submission and review history with comments. Click a report to expand.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {paginatedReports.map(r => {
              const isExpanded = expandedReport === r.report_id;
              return (
                <div key={r.report_id} className="border rounded-lg overflow-hidden" data-testid={`report-row-${r.report_id}`}>
                  {/* Summary row */}
                  <div
                    className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setExpandedReport(isExpanded ? null : r.report_id)}
                  >
                    {isExpanded
                      ? <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      : <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{r.stock_number}</span>
                        <Badge variant="outline" className="text-[10px]">{r.scope}</Badge>
                        {statusBadge(r.status)}
                        {r.first_time_approval && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
                            First-Time Approval
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {r.site_name} | {r.client_name} | {r.audit_date ? format(parseISO(r.audit_date), 'd MMM yyyy') : '\u2014'}
                        {' | '}{r.auditor_names?.join(', ') || '\u2014'}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground flex-shrink-0">
                      <span className="flex items-center gap-1" title="Submissions">
                        <Send className="h-3 w-3" />{r.submission_count}
                      </span>
                      {r.return_count > 0 && (
                        <span className="flex items-center gap-1 text-amber-600" title="Returns">
                          <RotateCcw className="h-3 w-3" />{r.return_count}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Expanded timeline */}
                  {isExpanded && r.timeline?.length > 0 && (
                    <div className="border-t bg-slate-50/50 px-4 py-3 space-y-2" data-testid={`timeline-${r.report_id}`}>
                      <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Approval Timeline</span>
                      <div className="relative ml-2 border-l-2 border-slate-200 space-y-3 pl-4">
                        {r.timeline.map((evt, i) => (
                          <div key={i} className="relative" data-testid={`timeline-event-${r.report_id}-${i}`}>
                            <div className="absolute -left-[21px] top-1 w-2.5 h-2.5 rounded-full border-2 border-white bg-slate-300" />
                            <div className="flex items-center gap-2 flex-wrap">
                              {actionBadge(evt.action_label)}
                              <span className="text-xs text-muted-foreground">
                                by <span className="font-medium text-slate-700">{evt.user_name}</span>
                                {evt.date && <span> on {format(parseISO(evt.date), 'd MMM yyyy, HH:mm')}</span>}
                              </span>
                            </div>
                            {evt.comments && (
                              <div className="mt-1.5 ml-1 px-3 py-2 bg-white border border-amber-200 rounded text-xs text-slate-700 italic" data-testid={`comment-${r.report_id}-${i}`}>
                                <MessageSquare className="h-3 w-3 inline mr-1 text-amber-500" />
                                {evt.comments}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {isExpanded && (!r.timeline || r.timeline.length === 0) && (
                    <div className="border-t bg-slate-50/50 px-4 py-3 text-xs text-muted-foreground">
                      No approval events recorded for this report.
                    </div>
                  )}
                </div>
              );
            })}

            {reportDetails.length === 0 && (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No reports submitted for approval in this period
              </div>
            )}

            {/* Pagination */}
            {reportDetails.length > pageSize && (
              <div className="flex items-center justify-between mt-4 print:hidden">
                <p className="text-sm text-muted-foreground">
                  Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, reportDetails.length)} of {reportDetails.length} reports
                </p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                    data-testid="quality-prev-page">
                    <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {page + 1} of {totalPages}
                  </span>
                  <Button variant="outline" size="sm"
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    data-testid="quality-next-page">
                    Next <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
